import { HttpClient } from '@angular/common/http';
import { toHttpParams } from './http-params';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';

export type DocumentAccessLevel = 'styre' | 'alle';

export interface FolderNode {
  id: string;
  name: string;
  parentId?: string | null;
  sortOrder: number;
  systemCategory?: string | null;
}

export interface DocumentSummary {
  id: string;
  title: string;
  folderId?: string | null;
  category?: string | null;
  accessLevel: DocumentAccessLevel;
  unitId?: string | null;
  currentVersionId: string;
  versionCount: number;
  tags: string[];
  updatedAtUtc: string;
}

export interface VersionView {
  versionId: string;
  mimeType: string;
  sizeBytes: number;
  uploadedBy: string;
  uploadedAtUtc: string;
  isCurrent: boolean;
}

export interface DocumentDetail {
  summary: DocumentSummary;
  versions: VersionView[];
}

export interface ArchiveTree {
  folders: FolderNode[];
  documents: DocumentSummary[];
}

export interface UploadMeta {
  title: string;
  accessLevel: DocumentAccessLevel;
  folderId?: string;
  category?: string;
  unitId?: string;
}

export interface DocumentPatch {
  title?: string;
  folderId?: string;
  accessLevel?: DocumentAccessLevel;
  tags?: string[];
}

/**
 * Document archive client (`/api/dokumenter`). Hand-written to mirror the Phase M4 backend
 * contract. Access control is server-side: a styre-only / foreign-unit document is absent
 * from {@link ArchiveTree}, so there is no client path that could reveal a restricted doc.
 */
@Injectable({ providedIn: 'root' })
export class DokumenterService {
  private readonly http = inject(HttpClient);

  /** Folder tree + the documents the caller may read (optionally within one folder). */
  tree(folderId?: string): Observable<ArchiveTree> {
    const params = toHttpParams({ folderId });
    return this.http.get<ArchiveTree>('/api/dokumenter', { params });
  }

  get(id: string): Observable<DocumentDetail> {
    return this.http.get<DocumentDetail>(`/api/dokumenter/${encodeURIComponent(id)}`);
  }

  /** The content-endpoint URL for a version. It requires the bearer token, so fetch it with {@link download}. */
  contentUrl(id: string, versionId?: string): string {
    const base = `/api/dokumenter/${encodeURIComponent(id)}`;
    return versionId
      ? `${base}/versjoner/${encodeURIComponent(versionId)}/innhold`
      : `${base}/innhold`;
  }

  /** Fetches a version's binary through HttpClient, so the auth interceptor attaches the bearer token. */
  download(id: string, versionId?: string): Observable<Blob> {
    return this.http.get(this.contentUrl(id, versionId), { responseType: 'blob' });
  }

  /** Creates a document + first version (multipart). The object key is server-generated. */
  upload(file: File, meta: UploadMeta): Observable<{ id: string }> {
    return this.http.post<{ id: string }>('/api/dokumenter', this.toForm(file, meta));
  }

  /** Appends a new version (multipart) and advances the current pointer. */
  addVersion(id: string, file: File): Observable<void> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<void>(`/api/dokumenter/${encodeURIComponent(id)}/versjoner`, form);
  }

  update(id: string, patch: DocumentPatch): Observable<void> {
    return this.http.put<void>(`/api/dokumenter/${encodeURIComponent(id)}`, patch);
  }

  softDelete(id: string): Observable<void> {
    return this.http.delete<void>(`/api/dokumenter/${encodeURIComponent(id)}`);
  }

  createFolder(name: string, parentId?: string, systemCategory?: string, sortOrder = 0): Observable<{ id: string }> {
    return this.http.post<{ id: string }>('/api/dokumenter/mapper', { name, parentId, systemCategory, sortOrder });
  }

  private toForm(file: File, meta: UploadMeta): FormData {
    const form = new FormData();
    form.append('file', file);
    form.append('title', meta.title);
    form.append('accessLevel', meta.accessLevel);
    if (meta.folderId) {
      form.append('folderId', meta.folderId);
    }
    if (meta.category) {
      form.append('category', meta.category);
    }
    if (meta.unitId) {
      form.append('unitId', meta.unitId);
    }
    return form;
  }
}
